import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Search,
    Users,
    RefreshCw,
    ChevronLeft,
    ChevronRight,
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Store,
    Eye,
    Package,
    Clock,
    DollarSign,
    Building,
    CheckCircle2,
    XCircle,
    Truck,
    Check,
    ChevronsUpDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers() {
    const [customers, setCustomers] = useState([]);
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedStore, setSelectedStore] = useState("all");
    const [page, setPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [showDetails, setShowDetails] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const queryParams = new URLSearchParams({
                page,
                limit: itemsPerPage,
                search: searchTerm,
                store_id: selectedStore === "all" ? "" : selectedStore
            });

            const response = await axios.get(`${API}/admin/customers?${queryParams}`, getAuthHeaders());
            setCustomers(response.data.customers || []);
            setTotalPages(response.data.pages || 1);
            setTotalCount(response.data.total || 0);
        } catch (error) {
            console.error("Failed to fetch customers:", error);
            toast.error("Failed to fetch customers");
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    }, [page, itemsPerPage, searchTerm, selectedStore]);

    const fetchStores = async () => {
        try {
            // Use existing endpoint to get stores for the filter
            const response = await axios.get(`${API}/admin/stores`, getAuthHeaders());
            setStores(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Failed to fetch stores:", error);
            setStores([]);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData();
        }, 400);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchData]);

    // Reset to first page when filtering changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm, selectedStore, itemsPerPage]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    const handleStoreFilter = (value) => {
        setSelectedStore(value);
        setPage(1);
    };

    const handleViewDetails = async (customer) => {
        setSelectedCustomer(customer);
        setShowDetails(true);
        setDetailsLoading(true);
        try {
            const res = await axios.get(`${API}/admin/customers/${customer._id}`, getAuthHeaders());
            setCustomerOrders(res.data.orders || []);
            // Update selected customer with possibly more details
            setSelectedCustomer(res.data.customer);
        } catch (error) {
            console.error("Failed to fetch customer details:", error);
            toast.error("Failed to load details");
        } finally {
            setDetailsLoading(false);
        }
    };

    const getDefaultAddress = (customer) => {
        if (!customer || !customer.addresses || !Array.isArray(customer.addresses) || customer.addresses.length === 0) return "No address";
        const addr = customer.addresses.find(a => a.is_default) || customer.addresses[0];
        if (!addr) return "No address";
        return `${addr.city || ""}, ${addr.state || ""}, ${addr.country || ""}`.replace(/^, |, $/g, "").trim() || "N/A";
    };

    return (
        <div className="space-y-6" data-testid="admin-customers">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                        Customer Management
                        <span className="text-sm font-normal bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                            {(totalCount || 0)} {totalCount === 1 ? 'Customer' : 'Customers'}
                        </span>
                    </h1>
                    <p className="text-slate-500">View and manage platform customers</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={fetchData} variant="outline" className="gap-2">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="relative md:col-span-2">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={handleSearch}
                            className="pl-10"
                        />
                    </div>
                    <Popover open={storeDropdownOpen} onOpenChange={setStoreDropdownOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={storeDropdownOpen}
                                className="w-full justify-between font-normal"
                            >
                                <span className="truncate">
                                    {selectedStore === "all"
                                        ? "All Stores"
                                        : stores.find((s) => s.store_id === selectedStore)?.business_name || "All Stores"}
                                </span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                            <Command>
                                <CommandInput placeholder="Search store..." />
                                <CommandList>
                                    <CommandEmpty>No store found.</CommandEmpty>
                                    <CommandGroup>
                                        <CommandItem
                                            value="all"
                                            onSelect={() => {
                                                handleStoreFilter("all");
                                                setStoreDropdownOpen(false);
                                            }}
                                        >
                                            <Check
                                                className={cn(
                                                    "mr-2 h-4 w-4",
                                                    selectedStore === "all" ? "opacity-100" : "opacity-0"
                                                )}
                                            />
                                            All Stores
                                        </CommandItem>
                                        {Array.isArray(stores) && stores.map((store) => (
                                            <CommandItem
                                                key={store.store_id || Math.random()}
                                                value={`${store.business_name} ${store.subdomain}`}
                                                onSelect={() => {
                                                    handleStoreFilter(store.store_id);
                                                    setStoreDropdownOpen(false);
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        "mr-2 h-4 w-4",
                                                        selectedStore === store.store_id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                <span className="truncate">
                                                    {store.business_name} ({store.subdomain})
                                                </span>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Customers Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold px-6 py-4">Customer</TableHead>
                            <TableHead className="font-semibold">Contact</TableHead>
                            <TableHead className="font-semibold">Store ID</TableHead>
                            <TableHead className="font-semibold">Type</TableHead>
                            <TableHead className="font-semibold">Default Address</TableHead>
                            <TableHead className="font-semibold">Joined</TableHead>
                            <TableHead className="font-semibold text-right">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12">
                                    <RefreshCw className="w-8 h-8 mx-auto text-primary animate-spin mb-3" />
                                    <p className="text-slate-500">Loading customers...</p>
                                </TableCell>
                            </TableRow>
                        ) : (!customers || customers.length === 0) ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                                    <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                                    <p>No customers found</p>
                                </TableCell>
                            </TableRow>
                        ) : (Array.isArray(customers) && customers.map((customer) => {
                            const store = Array.isArray(stores) ? stores.find(s => s.store_id === customer.store_id) : null;
                            return (
                                <TableRow key={customer._id} className="hover:bg-slate-50 transition-colors">
                                    <TableCell className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary/5 rounded-full flex items-center justify-center border border-primary/10">
                                                <User className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900">{customer?.name}</p>
                                                <Badge variant="outline" className="mt-1 text-[10px] h-4 uppercase tracking-wider font-bold">
                                                    #{customer?._id?.substring((customer?._id?.length || 6) - 6)}
                                                </Badge>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                <Mail className="w-3.5 h-3.5 text-slate-400" />
                                                {customer.email}
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                                <Phone className="w-3.5 h-3.5 text-slate-400" />
                                                {customer.phone || "N/A"}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <Store className="w-3.5 h-3.5 text-slate-400" />
                                            <span className="text-sm font-medium text-slate-700">
                                                {customer.store_id === "0" ? (
                                                    <span className="text-primary font-bold">e-Displayroom</span>
                                                ) : (
                                                    store ? store.business_name : customer.store_id
                                                )}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={customer.is_guest ? 'bg-slate-100 text-slate-600 border-none' : 'bg-green-100 text-green-700 border-none'}>
                                            {customer.is_guest ? 'Guest' : 'Registered'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-start gap-2 max-w-[200px]">
                                            <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                                            <span className="text-sm text-slate-600 line-clamp-2">
                                                {getDefaultAddress(customer)}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2 text-sm text-slate-600">
                                            <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                            {customer.created_at ? format(new Date(customer.created_at), 'MMM d, yyyy') : 'N/A'}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2 hover:bg-primary/5 hover:text-primary hover:border-primary/20"
                                            onClick={() => handleViewDetails(customer)}
                                        >
                                            <Eye className="w-4 h-4" />
                                            Details
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                        )}
                    </TableBody>
                </Table>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div className="px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t bg-slate-50/50 gap-4">
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                            <div>
                                Showing <span className="font-medium text-slate-900">{(page - 1) * itemsPerPage + 1}</span> to <span className="font-medium text-slate-900">{Math.min(page * itemsPerPage, totalCount)}</span> of <span className="font-medium text-slate-900">{totalCount}</span> results
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-slate-600">Rows per page:</span>
                                <Select
                                    value={itemsPerPage.toString()}
                                    onValueChange={(val) => setItemsPerPage(Number(val))}
                                >
                                    <SelectTrigger className="h-8 w-[70px]">
                                        <SelectValue placeholder="10" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="10">10</SelectItem>
                                        <SelectItem value="25">25</SelectItem>
                                        <SelectItem value="50">50</SelectItem>
                                        <SelectItem value="100">100</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                            </Button>

                            {/* Normalized Numbered Pagination Logic */}
                            <div className="hidden sm:flex items-center space-x-1">
                                {Array.from({ length: totalPages }).map((_, i) => {
                                    const pageNum = i + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= page - 1 && pageNum <= page + 1)
                                    ) {
                                        return (
                                            <Button
                                                key={pageNum}
                                                variant={page === pageNum ? "default" : "outline"}
                                                size="sm"
                                                className={`h-8 w-8 p-0 ${page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : ""}`}
                                                onClick={() => setPage(pageNum)}
                                            >
                                                {pageNum}
                                            </Button>
                                        );
                                    }
                                    if (pageNum === page - 2 || pageNum === page + 2) {
                                        return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                            >
                                Next <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Customer Details Modal */}
            <Dialog open={showDetails} onOpenChange={setShowDetails}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl flex items-center gap-2">
                            <User className="w-6 h-6 text-primary" />
                            Customer Details
                        </DialogTitle>
                        <DialogDescription>
                            Full profile and order history for {selectedCustomer?.name}
                        </DialogDescription>
                    </DialogHeader>

                    {detailsLoading ? (
                        <div className="py-20 text-center">
                            <RefreshCw className="w-10 h-10 mx-auto text-primary animate-spin mb-4" />
                            <p className="text-slate-500">Loading full profile...</p>
                        </div>
                    ) : (
                        <div className="space-y-8 py-4">
                            {/* Stats Overview */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 italic">
                                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                        <Package className="w-3.5 h-3.5" /> TOTAL ORDERS
                                    </div>
                                    <div className="text-xl font-bold text-slate-900">{customerOrders.length}</div>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                        <Clock className="w-3.5 h-3.5" /> RECENT ORDER
                                    </div>
                                    <div className="text-sm font-medium text-slate-900">
                                        {customerOrders.length > 0
                                            ? format(new Date(customerOrders[0].created_at), 'MMM d, yyyy')
                                            : 'No orders yet'}
                                    </div>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                        <DollarSign className="w-3.5 h-3.5" /> LIFETIME SPEND
                                    </div>
                                    <div className="text-xl font-bold text-green-600">
                                        ${customerOrders.reduce((sum, o) => sum + (o.total || 0), 0).toFixed(2)}
                                    </div>
                                </div>
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                        <Building className="w-3.5 h-3.5" /> STORE
                                    </div>
                                    <div className="text-sm font-medium text-slate-900 truncate">
                                        {selectedCustomer?.store_id === "0"
                                            ? "e-Displayroom"
                                            : stores.find(s => s.store_id === selectedCustomer?.store_id)?.business_name || selectedCustomer?.store_id}
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {/* Contact Info & Addresses */}
                                <div className="md:col-span-1 space-y-6">
                                    <section>
                                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Contact Information</h3>
                                        <div className="space-y-3 bg-white p-4 rounded-xl border border-slate-100">
                                            <div className="flex items-start gap-3">
                                                <Mail className="w-4 h-4 text-slate-400 mt-1" />
                                                <div>
                                                    <p className="text-xs text-slate-500">Email Address</p>
                                                    <p className="text-sm font-medium text-slate-900">{selectedCustomer?.email}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3 border-t pt-3">
                                                <Phone className="w-4 h-4 text-slate-400 mt-1" />
                                                <div>
                                                    <p className="text-xs text-slate-500">Phone Number</p>
                                                    <p className="text-sm font-medium text-slate-900">{selectedCustomer?.phone || 'N/A'}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-start gap-3 border-t pt-3">
                                                <Calendar className="w-4 h-4 text-slate-400 mt-1" />
                                                <div>
                                                    <p className="text-xs text-slate-500">Customer Since</p>
                                                    <p className="text-sm font-medium text-slate-900">
                                                        {selectedCustomer?.created_at ? format(new Date(selectedCustomer.created_at), 'MMMM d, yyyy') : 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex justify-between items-center">
                                            Saved Addresses
                                            <Badge variant="secondary" className="text-[10px]">{selectedCustomer?.addresses?.length || 0}</Badge>
                                        </h3>
                                        <div className="space-y-3">
                                            {selectedCustomer?.addresses?.length > 0 ? (
                                                selectedCustomer.addresses.map((addr, idx) => (
                                                    <div key={idx} className={`p-3 rounded-lg border text-sm ${addr.is_default ? 'bg-primary/5 border-primary/20 shadow-sm' : 'bg-white border-slate-100'}`}>
                                                        <div className="flex justify-between items-start mb-2">
                                                            <p className="font-bold text-slate-900">{addr.firstname} {addr.lastname}</p>
                                                            {addr.is_default && <Badge className="bg-primary text-[9px] h-4">DEFAULT</Badge>}
                                                        </div>
                                                        <p className="text-slate-600 leading-relaxed">
                                                            {addr.address}<br />
                                                            {addr.city}, {addr.state} {addr.zipcode}<br />
                                                            {addr.country}
                                                        </p>
                                                        {addr.phone && <p className="text-slate-400 text-xs mt-2 flex items-center gap-1">
                                                            <Phone className="w-3 h-3" /> {addr.phone}
                                                        </p>}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="p-8 text-center border-2 border-dashed rounded-xl bg-slate-50">
                                                    <MapPin className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                                                    <p className="text-xs text-slate-500">No addresses saved</p>
                                                </div>
                                            )}
                                        </div>
                                    </section>
                                </div>

                                {/* Order History */}
                                <div className="md:col-span-2 space-y-4">
                                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Order History</h3>
                                    <div className="border rounded-xl overflow-hidden bg-white shadow-sm">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="bg-slate-50">
                                                    <TableHead className="text-[11px] font-bold uppercase">Order ID</TableHead>
                                                    <TableHead className="text-[11px] font-bold uppercase">Date</TableHead>
                                                    <TableHead className="text-[11px] font-bold uppercase">Status</TableHead>
                                                    <TableHead className="text-[11px] font-bold uppercase">Items</TableHead>
                                                    <TableHead className="text-[11px] font-bold uppercase text-right">Total</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {customerOrders.length > 0 ? (
                                                    customerOrders.map((order) => (
                                                        <TableRow key={order._id} className="hover:bg-slate-50 cursor-pointer">
                                                            <TableCell className="font-medium text-xs text-primary">
                                                                #{order.order_number || order.order_id?.substring(0, 8)}
                                                            </TableCell>
                                                            <TableCell className="text-xs text-slate-500">
                                                                {format(new Date(order.created_at), 'MMM d')}
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge
                                                                    variant="outline"
                                                                    className={`text-[10px] h-5 ${order.status === 'delivered' ? 'border-green-200 text-green-700 bg-green-50' :
                                                                        order.status === 'cancelled' ? 'border-red-200 text-red-700 bg-red-50' :
                                                                            'border-primary/20 text-primary bg-primary/5'
                                                                        }`}
                                                                >
                                                                    {order.status}
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className="text-xs text-slate-500">
                                                                {order.items?.length || 0} items
                                                            </TableCell>
                                                            <TableCell className="text-right font-bold text-xs text-slate-900">
                                                                ${(order.total || 0).toFixed(2)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell colSpan={5} className="text-center py-12 text-slate-400 text-sm">
                                                            No purchase history found
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
